<ng-container *ngIf="project && dataReady">

  <header>
    <h2>{{ 'project.create.' + specificationFormRule?.keys?.header | translate }}</h2>
  </header>

  <form [formGroup]="specificationFormGroup">

    <section>
      <h4>{{ 'project.create.' + specificationFormRule?.keys?.info | translate }}</h4>
      <div class="basic-inputs">

        <!-- ROW 1: Program (1/3) + Project name (2/3). Program FIRST (FR-29): the program
             decides the workflow, and VIDA's prefill can fill the name — asking for the name
             before the program invited typing a value the link then overwrites. -->
        <ng-container *ngIf="specificationFormRule?.fields?.program?.display && programs?.length > 0">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.program?.required }" for="program">
              {{ 'project.create.program' | translate }}
            </label>
            <!-- oes-custom-dropdown per repo standard. The NO_PROGRAM sentinel survives the
                 migration because it is a non-empty string: normalizeInput() coerces null/undefined
                 to '' (which is our UNCHOSEN state) and isPlaceholderActive is !value, so an
                 explicit "No Program" still reads as answered. Expired programs stay visible but
                 unselectable via IMultiSelectOption.disabled. -->
            <oes-custom-dropdown
              formControlName="programControl"
              data-testid="project-program"
              [options]="programOptions"
              [searchable]="true"
              placeholderKey="form.select-default">
            </oes-custom-dropdown>
          </div>
        </ng-container>

        <ng-container *ngIf="specificationFormRule?.fields?.name?.display">
          <div class="input-66">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.name?.required }" for="name">
              {{ 'project.create.' + specificationFormRule?.fields?.name?.headingKey | translate }}
            </label>
            <input
              type="text"
              class="form-control"
              id="name"
              data-testid="project-name"
              formControlName="nameControl">
            <oes-form-error-message
              [showRequired]="specificationFormGroup?.controls?.nameControl?.touched &&
                              specificationFormGroup?.controls?.nameControl?.invalid">
            </oes-form-error-message>
          </div>
        </ng-container>

        <!-- DT-6472 FR-29: the external-prefill shell — VIDA's stage one is program + slug.
             Renders only when the selected program's workflow supplies a provider; every other
             program is untouched (the shell's *ngIf="provider" is load-bearing). -->
        <ng-container *ngIf="showPrefillShell">
          <div class="input-100" data-testid="external-prefill-host">
            <oes-external-prefill
              [provider]="prefillProvider"
              [programId]="selectedProgramIdForPrefill"
              [blockComponent]="prefillBlockComponent"
              [disabled]="readOnly"
              (confirmed)="onPrefillConfirmed($event)"
              (cleared)="onPrefillCleared()">
            </oes-external-prefill>
          </div>
        </ng-container>

        <!-- Empty state: the program question is unanswered. Gates only the rule-driven fields;
             Cancel/Create and the error summary stay OUTSIDE it, per the mockup, so a
             "Program is required" message still has somewhere to appear. -->
        <ng-container *ngIf="showSelectProgramEmptyState">
          <div class="input-100 select-program-empty-state" role="status" data-testid="select-program-empty-state">
            <div class="select-program-empty-state__title">{{ 'project.create.select-program-title' | translate }}</div>
            <div class="select-program-empty-state__body">{{ 'project.create.select-program-body' | translate }}</div>
          </div>
        </ng-container>

        <!-- Workflow misconfiguration: the program names a workflow this build does not have, and
             there is no project type to fall back to. Blocking rather than a half-built form. -->
        <ng-container *ngIf="workflowConfigurationError">
          <div class="input-100 workflow-configuration-error" role="alert" data-testid="workflow-configuration-error">
            {{ workflowConfigurationError.message }}
          </div>
        </ng-container>

        <!-- DIVIDER + PROGRAM PILL. Everything below is "the details this program asks for", which
             is why the divider and pill sit between the identity row and the rule-driven fields. -->
        <ng-container *ngIf="showRuleDrivenSections">
          <div class="input-100 program-divider"><hr></div>
        </ng-container>

        <ng-container *ngIf="showProgramPill">
          <div class="input-100 program-pill-row" data-testid="program-pill-row">
            <span class="program-pill">{{ selectedProgramName }}</span>
            <span class="program-pill-note">{{ 'project.create.program-additional-details' | translate }}</span>
          </div>
        </ng-container>

        <!-- ROW 2: Asset type (1/3), Status (1/3), COD (1/3).
             Asset type is display only — the type is chosen at the entry point and has never been
             user-selectable here. The control stays in the group; getFormValues reads it unguarded. -->
        <ng-container *ngIf="showRuleDrivenSections && specificationFormRule?.fields?.projectType?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.projectType?.required }" for="projectType">
              {{ 'project.create.type' | translate }}
            </label>
            <oes-locked-field
              id="projectType"
              data-testid="project-type"
              [value]="project?.projectType ? ('project.types.' + project.projectType | translate) : ''"
              [tooltip]="'project.create.locked-field-tooltip' | translate">
            </oes-locked-field>
          </div>
        </ng-container>


        <!-- Status -->
        <ng-container *ngIf="showRuleDrivenSections && specificationFormRule?.fields?.status?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.status?.required }" for="status">
              {{ 'project.create.status' | translate }}
            </label>
            <oes-custom-dropdown
              formControlName="statusControl"
              data-testid="project-status"
              [options]="statusOptions"
              [searchable]="true"
              placeholderKey="form.select-default">
            </oes-custom-dropdown>
          </div>
        </ng-container>

        <!-- COD -->
        <ng-container *ngIf="showRuleDrivenSections && specificationFormRule?.fields?.cod?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.cod?.required }" for="cod">
              {{ 'project.create.' + specificationFormRule?.fields?.cod?.headingKey | translate }}
            </label>
            <div class="calendar-wrapper">
              <input
                type="text"
                id="cod"
                data-testid="project-calendar"
                formControlName="codControl"
                class="form-control"
                autocomplete="off"
                [placeholder]="'calendar.format' | translate"
                bsDatepicker
                [bsConfig]="bsConfig"
                placement="top">
            </div>
            <oes-form-error-message
              [showRequired]="specificationFormGroup?.controls?.codControl?.touched &&
                              specificationFormGroup?.controls?.codControl?.invalid">
            </oes-form-error-message>
          </div>
        </ng-container>

        <!-- Tender Project -->
        <ng-container *ngIf="showRuleDrivenSections && specificationFormRule?.fields?.tenderProject?.display">
          <ng-container *ngIf="showTenderProjectField">
            <div class="input-100 tender-project-row">
              <input
                type="checkbox"
                id="tenderProject"
                data-testid="tender-project"
                formControlName="tenderProjectControl"
                class="form-control style-overhaul-flag" />
              <label class="tender-project-label" [ngClass]="{ 'required' : specificationFormRule?.fields?.tenderProject?.required }" for="tenderProject">
                {{ 'project.create.tender-project' | translate }}
              </label>
              <oes-form-error-message
                [showRequired]="specificationFormGroup?.controls?.tenderProjectControl?.touched &&
                                specificationFormGroup?.controls?.tenderProjectControl?.invalid">
              </oes-form-error-message>
            </div>
          </ng-container>
        </ng-container>

        <!-- Description -->
        <ng-container *ngIf="showRuleDrivenSections && specificationFormRule?.fields?.description?.display">
          <div class="input-100">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.description?.required }" for="description">
              {{ 'project.create.description' | translate }}
            </label>
            <oes-quill
              id="description"
              class="style-overhaul-flag"
              data-testid="project-description"
              formControlName="descriptionControl"
              [placeholder]="'project.create.executive-summary' | translate"
              [editorDisabled]="readOnly"
              [initialValue]="project?.description || ''">
            </oes-quill>
          </div>
        </ng-container>

      </div>
    </section>

    <section *ngIf="showRuleDrivenSections && (specificationFormRule?.fields?.address1?.display ||
                    specificationFormRule?.fields?.address2?.display ||
                    specificationFormRule?.fields?.country?.display ||
                    specificationFormRule?.fields?.state?.display ||
                    specificationFormRule?.fields?.city?.display ||
                    specificationFormRule?.fields?.postalCode?.display ||
                    specificationFormRule?.fields?.latitude?.display ||
                    specificationFormRule?.fields?.longitude?.display)">
      <h4>{{ 'project.create.' + specificationFormRule?.keys?.location | translate }}</h4>
      <div class="location-inputs">

        <!-- Address 1 -->
        <ng-container *ngIf="specificationFormRule?.fields?.address1?.display">
          <div class="input-50">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.address1?.required }" for="address1">
              {{ 'project.create.address-1' | translate }}
            </label>
            <input
              type="text"
              class="form-control"
              id="address1"
              data-testid="project-address-1"
              formControlName="address1Control">
          </div>
        </ng-container>

        <!-- Address 2 -->
        <ng-container *ngIf="specificationFormRule?.fields?.address2?.display">
          <div class="input-50">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.address2?.required }" for="address2">
              {{ 'project.create.address-2' | translate }}
            </label>
            <input
              type="text"
              class="form-control"
              id="address2"
              data-testid="project-address-2"
              formControlName="address2Control">
          </div>
        </ng-container>

        <!-- Country -->
        <ng-container *ngIf="specificationFormRule?.fields?.country?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.country?.required }" for="country">
              {{ 'project.create.country' | translate }}
            </label>
            <oes-custom-dropdown
              formControlName="countryControl"
              data-testid="project-country"
              [options]="countryOptions"
              [searchable]="true"
              placeholderKey="form.select-default">
            </oes-custom-dropdown>
            <oes-form-error-message
              [showRequired]="specificationFormGroup?.controls?.countryControl?.touched &&
                              specificationFormGroup?.controls?.countryControl?.invalid">
            </oes-form-error-message>
          </div>
        </ng-container>

        <!-- State/Province -->
        <ng-container *ngIf="specificationFormRule?.fields?.state?.display">
          <div class="input-33 state">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.state?.required }" for="region">
              {{ 'project.create.region' | translate }}
            </label>
            <oes-custom-dropdown
              formControlName="regionControl"
              data-testid="project-region"
              [options]="regionOptions"
              [searchable]="true"
              placeholderKey="form.select-default">
            </oes-custom-dropdown>
          </div>
        </ng-container>

        <!-- City -->
        <ng-container *ngIf="specificationFormRule?.fields?.city?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.city?.required }" for="city">
              {{ 'project.create.city' | translate }}
            </label>
            <input
              id="city"
              data-testid="project-city"
              type="text"
              class="form-control"
              formControlName="cityControl">
            <oes-form-error-message
              [showRequired]="specificationFormGroup?.controls?.cityControl?.touched &&
                              specificationFormGroup?.controls?.cityControl?.invalid">
            </oes-form-error-message>
          </div>
        </ng-container>

        <!-- PostalCode -->
        <ng-container *ngIf="specificationFormRule?.fields?.postalCode?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.postalCode?.required }" for="postalCode">
              {{ 'project.create.postal-code' | translate }}
            </label>
            <input
              type="text"
              class="form-control"
              id="postalCode"
              data-testid="project-postal-code"
              formControlName="postalCodeControl">
          </div>
        </ng-container>

        <!-- Latitude -->
        <ng-container *ngIf="specificationFormRule?.fields?.latitude?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.latitude?.required }" for="latitude">{{ 'project.create.latitude' | translate }}</label>
            <input
              type="number"
              data-testid="project-latitude"
              id="latitude"
              class="form-control"
              formControlName="latitudeControl">
            <oes-form-error-message
              [showRequired]="specificationFormGroup?.controls?.latitudeControl?.touched &&
                              specificationFormGroup?.controls?.latitudeControl?.invalid">
            </oes-form-error-message>
          </div>

        </ng-container>

        <!-- Longitude -->
        <ng-container *ngIf="specificationFormRule?.fields?.longitude?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.longitude?.required }" for="longitude">{{ 'project.create.longitude' | translate }}</label>
            <input
              type="number"
              data-testid="project-longitude"
              id="longitude"
              class="form-control"
              formControlName="longitudeControl">
            <oes-form-error-message
              [showRequired]="specificationFormGroup?.controls?.longitudeControl?.touched &&
                              specificationFormGroup?.controls?.longitudeControl?.invalid">
            </oes-form-error-message>
          </div>
        </ng-container>

        <!-- Map link. Its own column, so it sits to the RIGHT of longitude rather than under it. -->
        <ng-container *ngIf="specificationFormRule?.fields?.map?.display">
          <div class="input-33 check-location-column">
            <p class="check-location" (click)="checkLocation()">{{ 'project.create.check-location' | translate }}<img src="/assets/icons/line-icons/shared/orange/arrow-up-right.svg" /></p>
          </div>
        </ng-container>

      </div>
    </section>

    <section *ngIf="showRuleDrivenSections && (specificationFormRule?.fields?.currency?.display ||
                    specificationFormRule?.fields?.exchangeRate?.display)">
      <h4>{{ 'project.create.' + specificationFormRule?.keys?.currency | translate }}</h4>
      <div class="currency-inputs">

        <!-- Currency -->
        <ng-container *ngIf="specificationFormRule?.fields?.currency?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.currency?.required }" for="alternate-currency">
              {{ 'project.create.' + specificationFormRule?.fields?.currency?.headingKey | translate }}
            </label>
            <oes-custom-dropdown
              formControlName="currencyControl"
              data-testid="project-alternate-currency"
              [options]="currencyOptions"
              [searchable]="true"
              placeholderKey="form.select-default">
            </oes-custom-dropdown>
          </div>
        </ng-container>

        <!-- Exchange rate -->
        <ng-container *ngIf="specificationFormRule?.fields?.exchangeRate?.display">
          <div class="input-33">
            <label [ngClass]="{ 'required' : specificationFormRule?.fields?.exchangeRate?.required }" for="exchange-rate">
              {{ 'project.create.exchange-rate' | translate }}
              <a class="view-current-rates" (click)="showExchangeRates()">
                {{ 'project.create.exchange-rate-link' | translate }}
              </a>
            </label>
            <input
              type="number"
              data-testid="project-exchange-rate"
              id="exchange-rate"
              class="form-control"
              min="0"
              formControlName="exchangeRateControl">
            <oes-form-error-message
              [showInvalid]="specificationFormGroup?.controls?.countryControl?.dirty &&
                            specificationFormGroup?.controls?.exchangeRateControl?.errors?.validNumber"
              [text]="'form.error.greater-than-zero' | translate">
            </oes-form-error-message>
          </div>
        </ng-container>

      </div>
    </section>
  </form>

  <!-- Consolidated error summary. Sits OUTSIDE the reveal gate, per the mockup — it is the only
       path by which a "Program is required" message can appear before stage two opens. -->
  <ng-container *ngIf="!readOnly && showErrorSummary">
    <div class="form-error-summary" role="alert" data-testid="form-error-summary">
      {{ errorSummaryText }}
    </div>
  </ng-container>

  <!-- buttons -->
  <ng-container *ngIf="!readOnly">
    <div class="buttons d-flex flex-row justify-content-end">
      <ng-container *ngIf="cancelButton">
        <button type="button"
                class="btn btn-secondary"
                data-testid="cancel-create-project-button"
                (click)="cancel()">
          {{ 'buttons.cancel' | translate }}
        </button>
      </ng-container>
      <button type="submit"
              class="btn btn-primary"
              data-testid="save-new-project-button"
              [disabled]="!specificationFormGroup?.valid || !specificationFormGroup.dirty
                          || awaitingPrefillConfirmation"
              (click)="onSubmit()">
        {{ 'buttons.' + submitButtonName | translate }}
      </button>
    </div>
  </ng-container>

</ng-container>

<!-- VIDA type-mismatch notice (dev-demo item 6): the provider's type is authoritative. -->
<ng-container *ngIf="typeMismatch">
  <oes-modal-dialog
    #typeMismatchModal
    data-testid="type-mismatch-modal"
    [title]="'project.create.external-prefill.type-mismatch-title' | translate"
    [allowCancel]="true"
    [allowSubmit]="false"
    [cancelButtonText]="'buttons.close' | translate"
    [size]="'small'">
    <p>{{ 'project.create.external-prefill.type-mismatch-body' | translate
        : { returned: ('project.types.' + typeMismatch.returned | translate),
            chosen: ('project.types.' + typeMismatch.chosen | translate) } }}</p>
  </oes-modal-dialog>
</ng-container>

<!-- Google Maps Modal -->
<ng-container *ngIf="specificationFormRule?.fields?.map?.display">
  <oes-modal-dialog
    #googleMapsModal
    [title]="'project.create.map' | translate"
    [allowCancel]="true"
    [allowSubmit]="false"
    [cancelButtonText]="'buttons.close' | translate"
    [size]="'small'">
    <google-map
      height="300px"
      [options]="mapOptions"
      (mapClick)="addMarker($event)">
      <map-marker
        [position]="markerPosition"
        [options]="markerOptions">
      </map-marker>
    </google-map>
  </oes-modal-dialog>
</ng-container>

<!-- Exchange Rate Modal -->
<ng-container *ngIf="specificationFormRule?.fields?.exchangeRate?.display">
  <oes-modal-dialog
    #exchangeRateModal
    [title]="'project.create.exchange-rate-modal.title' | translate"
    [allowCancel]="true"
    [allowSubmit]="false"
    [size]="'small'">
    <table>
      <tr>
        <th>{{ 'project.create.code' | translate }}</th>
        <th>{{ 'project.create.currency' | translate }}</th>
        <th>{{ 'project.create.exchange-rate-per-usd' | translate }}</th>
        <th>{{ 'project.create.date' | translate }}</th>
      </tr>
      <tr *ngFor="let currencyExchange of exchangeRates">
        <td>{{ currencyExchange.code }}</td>
        <td>{{ 'currency-type.'+currencyExchange.code | translate }}</td>
        <td>{{ currencyExchange.exchangeRate }}</td>
        <td>{{ currencyExchange.quoteDateTime | date: 'yyyy-MM-dd' }}</td>
      </tr>
    </table>
  </oes-modal-dialog>
</ng-container>
